/**
 * MOTH (Machine-Optimized Text Hierarchy) Converter
 * Converts markdown/text files to MOTH format using an LLM
 *
 * IMPORTANT: MOTH files must ONLY be created by LLMs using the moth-onboarding.txt prompt
 * This ensures proper compression, structure, and adherence to MOTH standards
 */

import { smartChatCompletion } from '@/providers/llm-provider'

/**
 * The canonical MOTH onboarding prompt
 * This must be used for all MOTH conversions to ensure proper format
 *
 * Source: moth-onbaording.txt in repository root
 *
 * Note: In a browser environment, we embed the prompt directly.
 * For updates, sync with moth-onbaording.txt
 */
const MOTH_ONBOARDING_PROMPT = `# MOTHIFY LLM Onboarding Prompt

You are now a MOTH (Machine-Optimized Text Hierarchy) conversion expert. Your role is to transform verbose text and markdown documentation into compact, structured .moth files optimized for AI coding agents.

## [CORE_MISSION]
Convert documentation → .moth format
Maximize compression while preserving all critical information
Output deterministic, parseable, low-entropy specifications

## [FILE_EXTENSIONS]

Save all MOTH files with the .moth extension.

## [MOTH_SYNTAX]

### Sections
[SECTION] - Top-level section in ALL_CAPS
[SECTION.subsection] - Nested subsection

### Key-Value Pairs
key:value - Basic assignment
key:item1;item2;item3 - Semicolon-separated lists
key:{k=v;k2=v2} - State maps for configuration

### Comments
# Comment lines start with hash

### Naming Conventions
- Keys: snake_case
- Sections: UPPERCASE with optional .subsection
- Values: lowercase, use underscores for multi-word

## [CONVERSION_RULES]

### Compression Strategies
1. Remove all prose and filler words
2. Convert sentences to key:value pairs
3. Group related items with semicolons
4. Use abbreviations: db→database, auth→authentication, env→environment
5. Combine related concepts: frontend:react+typescript (not separate lines)
6. Target 70-90% size reduction vs original

### Structure Mapping
- **Headings** → [SECTIONS]
- **Bullet lists** → key:item1;item2;item3
- **Tables** → Multiple key:value pairs or state maps
- **Paragraphs** → Extract facts into key:value
- **Code blocks** → Summarize intent, not implementation

### Validation
- No duplicate keys within same section
- All sections must be [UPPERCASE] or [UPPERCASE.lowercase]
- Keys must be snake_case
- Lists use semicolons, not commas
- State maps use {k=v;k2=v2} format

## [BEST_PRACTICES]

1. **Always include intent header**: intent:type; audience:target
2. **Favor specificity**: "db:postgresql15" not "db:postgres"
3. **Use state maps for config**: {enabled=true;timeout=30s}
4. **Group related items**: frontend:react+typescript+tailwind
5. **FORBIDDEN section is critical**: Always include for rules files
6. **Keep files focused**: Split large specs into modules
7. **Validate output**: Check for duplicates, proper formatting
8. **Preserve all requirements**: Compression ≠ information loss

## [WORKFLOW]

1. **Analyze input**: Identify document type (PRD, rules, API, architecture)
2. **Extract structure**: Map headings to sections, content to key:value
3. **Compress**: Remove prose, combine related items, use abbreviations
4. **Validate**: Check syntax, no duplicates, proper section names
5. **Output**: Clean .moth file with proper formatting

You are now ready to convert documentation to MOTH format!

When given input:
1. Identify the document type
2. Convert to appropriate .moth structure
3. Validate syntax
4. Output clean, compressed .moth file - ONLY the .moth content, no explanations`

export interface MothConversionOptions {
  model?: string
  apiKey?: string
  intent?: string // Override intent type (e.g., 'coding_rules', 'product_requirements')
}

/**
 * Convert markdown/text content to MOTH format using an LLM
 *
 * CRITICAL: This function uses the moth-onboarding.txt prompt to ensure
 * proper MOTH format conversion by an LLM. Never manually create MOTH files.
 */
export async function convertToMoth(
  content: string,
  options: MothConversionOptions = {}
): Promise<string> {
  const { model = 'deepseek/deepseek-chat', apiKey, intent } = options

  // Add intent hint if provided
  const userPrompt = intent
    ? `Convert this to MOTH format with intent:${intent}\n\nOutput ONLY the .moth content, no explanations.\n\n${content}`
    : `Convert this to MOTH format. Output ONLY the .moth content, no explanations.\n\n${content}`

  try {
    const response = await smartChatCompletion({
      model,
      messages: [
        { role: 'system', content: MOTH_ONBOARDING_PROMPT },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.1, // Low temperature for deterministic output
      maxTokens: 4000,
    })

    if (!(response as any).success || !response.content) {
      throw new Error((response as any).error || 'Failed to convert to MOTH format')
    }

    return response.content.trim()
  } catch (error) {
    console.error('[MOTH] Conversion failed:', error)
    throw error
  }
}

/**
 * Detect the intent type from markdown content
 */
export function detectIntent(content: string): string {
  const lower = content.toLowerCase()

  if (lower.includes('architecture') || lower.includes('system design')) {
    return 'system_architecture'
  }
  if (lower.includes('decision') || lower.includes('adr')) {
    return 'architecture_decisions'
  }
  if (lower.includes('convention') || lower.includes('code style') || lower.includes('coding rules')) {
    return 'coding_rules'
  }
  if (lower.includes('memory') || lower.includes('context')) {
    return 'project_memory'
  }
  if (lower.includes('api') || lower.includes('endpoint')) {
    return 'api_specification'
  }
  if (lower.includes('product') || lower.includes('requirements') || lower.includes('prd')) {
    return 'product_requirements'
  }

  return 'documentation'
}

/**
 * Convert a file to MOTH format with automatic intent detection
 */
export async function convertFileToMoth(
  filePath: string,
  content: string,
  options: MothConversionOptions = {}
): Promise<{ mothContent: string; intent: string }> {
  const intent = options.intent || detectIntent(content)
  const mothContent = await convertToMoth(content, { ...options, intent })

  return { mothContent, intent }
}

/**
 * Batch convert multiple files to MOTH format
 */
export async function batchConvertToMoth(
  files: Array<{ path: string; content: string }>,
  options: MothConversionOptions = {}
): Promise<Array<{ path: string; mothPath: string; mothContent: string; intent: string }>> {
  const results = []

  for (const file of files) {
    try {
      const { mothContent, intent } = await convertFileToMoth(file.path, file.content, options)
      const mothPath = file.path.replace(/\.(md|txt)$/, '.moth')

      results.push({
        path: file.path,
        mothPath,
        mothContent,
        intent,
      })
    } catch (error) {
      console.error(`[MOTH] Failed to convert ${file.path}:`, error)
      // Continue with other files
    }
  }

  return results
}

